import {
  Box,
  Center,
  CircularProgress,
  HStack,
  Stack,
  useToast,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import {
  Button,
  Divider,
  DropResult,
  showToast,
  TabPanel,
  TabPanels,
  Text,
} from '@diamond/shared/ui';
import {
  adminCaptureException,
  LIST_FEATURE_FLAGS,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { PillTabs } from '@diamond/sol-admin/components';
import { useBusinessTypes } from '@diamond/sol-admin/features/shipto';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';

import { useProductHighlightDetail } from '../../api/get-highlight-detail';
import { useProductHighlightItem } from '../../api/get-highlight-item';
import {
  useAddHighlightItems,
  useCreateProductHighlight,
  useDeleteHighlightItems,
  useReorderHighlightItem,
  useUpdateProductHighlight,
} from '../../hooks/index';
import { FormValidationSchema } from '../../schema/index';
import {
  FormValues,
  HighlightFormValues,
  ProductsFieldArray,
} from '../../types';
import {
  mapFormToCreatePayload,
  mapFormToUpdatePayload,
} from '../../utils/mapping';
import { FiltersGroup } from './filters-group';
import { ProductList } from './product-list';
import { ProductRow } from './product-row';
import { ProductSelector } from './product-selector';
import { ShipToCSV } from './ship-to-csv';
import { TitleField } from './title-field';

export interface FormHighlightProps {
  id: string;
  modalState: 'edit' | 'create' | 'duplicate';
  allProductHighlightData: { id: string; title: string }[];
  onClose: () => void;
  refetch: () => void;
}

export function FormHighlight({
  id,
  modalState,
  allProductHighlightData,
  onClose,
  refetch,
}: FormHighlightProps) {
  const toast = useToast();
  const isNewCg3 = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_HIGHLIGHT_CG3_NEW,
    false
  );

  const selectedProductRef = useRef<SelectOption | null>(null);
  const createHighlight = useCreateProductHighlight();

  const methods = useForm<FormValues>({
    resolver: yupResolver(FormValidationSchema),
    mode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    defaultValues: {
      mode: 'general',
      title: '',
      distribution_channel: [],
      business_segmentation: [],
      products: [] as ProductsFieldArray,
    } as unknown as FormValues,
  });

  const { handleSubmit, reset, control, setValue, formState } = methods;
  const { errors, isValid, isSubmitted } = formState;

  const selectedProductId = useWatch({ control, name: 'product' });
  const mode = useWatch({ control, name: 'mode' });

  const { data: dataItem, isFetching } = useProductHighlightItem(id, {
    size: 999,
    page: 1,
  });

  const { data: detailData } = useProductHighlightDetail(id);

  const { options: businessTypeOptions } = useBusinessTypes({
    enabled: isNewCg3,
  });
  const businessTypeMap = useMemo(() => {
    return (businessTypeOptions || []).reduce<Record<string, string>>(
      (acc, opt) => {
        acc[opt.value] = opt.label;
        return acc;
      },
      {}
    );
  }, [businessTypeOptions]);

  const { fields, prepend, remove, move, replace } = useFieldArray({
    control,
    name: 'products',
    shouldUnregister: true,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const addHighlightItems = useAddHighlightItems();
  const deleteHighlightItems = useDeleteHighlightItems();
  const reorderHighlightItem = useReorderHighlightItem();

  const productIds = useMemo(
    () => new Set(fields.map((field) => field.product_id)),
    [fields]
  );

  const addProduct = useCallback(async () => {
    const item = selectedProductId;

    if (!item) return;

    if (productIds.has(item)) {
      showToast(toast, 'warning', 'Produk sudah ditambahkan');
      return;
    }

    prepend({
      product_id: item,
      product_title: selectedProductRef.current?.label ?? '',
    });

    if (modalState === 'edit') {
      await addHighlightItems.mutateAsync({
        highlightId: id,
        products: [item],
      });
    }
  }, [
    selectedProductId,
    productIds,
    prepend,
    addHighlightItems,
    modalState,
    id,
    toast,
  ]);

  const removeProduct = useCallback(
    async (index: number, productId: string) => {
      if (modalState === 'edit') {
        await deleteHighlightItems.mutateAsync({
          highlightId: id,
          products: [productId],
        });
      }
      remove(index);
    },
    [deleteHighlightItems, id, modalState, remove]
  );

  const updateHighlight = useUpdateProductHighlight();

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      setIsSubmitting(true);
      if (modalState === 'create' || modalState === 'duplicate') {
        const basePayload = mapFormToCreatePayload(
          values as HighlightFormValues,
          allProductHighlightData.length + 1,
          businessTypeMap
        );
        const mappedBusinessTypes = basePayload.business_types?.map((bt) => ({
          ...bt,
          cg_3_business_type_desc:
            businessTypeMap[bt.cg_3_business_type] ||
            bt.cg_3_business_type_desc ||
            '',
        }));
        const payload = {
          ...basePayload,
          ...(mappedBusinessTypes && mappedBusinessTypes.length > 0
            ? { business_types: mappedBusinessTypes }
            : {}),
        };
        await createHighlight.mutateAsync(payload);
        const successMessage =
          modalState === 'duplicate'
            ? 'Produk Highlight berhasil diduplikasi!'
            : 'Produk Highlight berhasil dibuat!';
        showToast(toast, 'success', successMessage);
        onClose();
        refetch();
      } else if (modalState === 'edit') {
        const basePayload = mapFormToUpdatePayload(
          values as HighlightFormValues
        );
        const mappedBusinessTypes = basePayload.business_types?.map((bt) => ({
          ...bt,
          cg_3_business_type_desc:
            businessTypeMap[bt.cg_3_business_type] ||
            bt.cg_3_business_type_desc ||
            '',
        }));
        const payload = {
          ...basePayload,
          ...(mappedBusinessTypes && mappedBusinessTypes.length > 0
            ? { business_types: mappedBusinessTypes }
            : {}),
        };
        await updateHighlight.mutateAsync({ id, payload });
        showToast(toast, 'success', 'Produk Highlight berhasil diperbarui!');
        onClose();
        refetch();
      }
    } catch (error: unknown) {
      const getErrorMessage = (error: unknown): string => {
        if (axios.isAxiosError(error)) {
          const data = error.response?.data as Record<string, unknown>;
          const backendMsg =
            (typeof data?.message === 'string' && data.message) ||
            (Array.isArray(data?.message) && data.message.join(', ')) ||
            (typeof data?.error === 'string' && data.error) ||
            (typeof data?.detail === 'string' && data.detail);

          return backendMsg || 'Terjadi kesalahan. Coba lagi.';
        }

        return error instanceof Error
          ? error.message
          : 'Terjadi kesalahan. Coba lagi.';
      };

      const errorMessage = getErrorMessage(error);

      const resp = axios.isAxiosError(error) ? error.response : undefined;
      const data = resp?.data as Record<string, unknown>;

      if (data?.errors && typeof data.errors === 'object') {
        const validFields: Array<keyof FormValues> = [
          'title',
          'mode',
          'product',
          'distribution_channel',
          'business_segmentation',
          'products',
          'business_types',
          'shiptos',
        ];

        Object.entries(data.errors).forEach(([name, msg]) => {
          if (validFields.includes(name as keyof FormValues)) {
            const message = Array.isArray(msg)
              ? msg.join(', ')
              : String(msg ?? '');
            methods.setError(name as keyof FormValues, {
              type: 'server',
              message,
            });
          }
        });
      }

      const action =
        modalState === 'create' ? 'createHighlight' : 'updateHighlight';
      const statusCode = resp?.status;

      adminCaptureException(
        {
          message: errorMessage,
          statusCode: statusCode ?? 500,
        },
        (scope) => {
          scope.setTags({
            feature: 'highlight',
            action,
            status_code: statusCode,
          });
          scope.setContext('error', { error: data || error });
          scope.setExtra(action, {
            highlightId: id,
            title: values.title,
            mode: values.mode,
            productsCount: values.products?.length ?? 0,
            modalState,
            response: data,
          });
          scope.setLevel('error');
        }
      );

      showToast(toast, 'error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isSubmitted || isValid) return;
    const firstError = Object.values(errors)[0];
    const message =
      (firstError?.message as string) || 'Periksa kembali input Anda';
    showToast(toast, 'error', message);
  }, [errors, isSubmitted, isValid, toast]);

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (modalState !== 'edit' && modalState !== 'duplicate') return;
    if (!detailData || id === '' || isFetching) return;

    reset(
      {
        title: detailData.title,
        mode: 'general' as const,
        business_types: Array.isArray(detailData.business_types)
          ? detailData.business_types.map((b) => b.cg_3_business_type)
          : [],
        business_segmentation: detailData.business_segmentation ?? [],
        distribution_channel: detailData.distribution_channel ?? [],
        products: [],
      },
      {
        keepIsSubmitted: true,
        keepTouched: true,
        keepDirty: false,
        keepDefaultValues: true,
      }
    );

    if (dataItem && Array.isArray(dataItem.data)) {
      const mapped = dataItem.data.map(
        (val: { id: string; title: string }) => ({
          product_id: val.id,
          product_title: val.title,
        })
      );
      replace(mapped);
    }

    initializedRef.current = true;
  }, [detailData, dataItem, id, modalState, reset, isFetching, replace]);

  const handleReorder = useCallback(
    async (result: DropResult) => {
      if (result.destination && modalState === 'edit') {
        await reorderHighlightItem.mutateAsync({
          highlightId: id,
          productId: fields[result.source.index].product_id,
          index: fields.length - result.destination.index,
        });
      }
    },
    [fields, id, modalState, reorderHighlightItem]
  );

  const items = useMemo(
    () =>
      fields.map((item, index) => ({
        id: `${item['product_id']}-${index}`,
        children: (
          <ProductRow
            key={item.id}
            index={index}
            productId={item['product_id']}
            onRemove={removeProduct}
          />
        ),
      })),
    [fields, removeProduct]
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack minH="96" marginTop={0} gap={6}>
          <Stack direction={{ base: 'column', md: 'row' }} gap={6}>
            <Box flex={1} minW={0}>
              <TitleField />

              <Box mt={4}>
                <ProductSelector
                  onPickChange={(option) => {
                    selectedProductRef.current = option ?? null;
                  }}
                  onAdd={addProduct}
                  isAddDisabled={Boolean(
                    !selectedProductId ||
                      selectedProductId === '' ||
                      (selectedProductId && productIds.has(selectedProductId))
                  )}
                />
              </Box>

              {isFetching && (
                <Center py="4">
                  <CircularProgress
                    isIndeterminate
                    color="blue"
                    size="40px"
                    aria-label="Memuat daftar produk"
                  />
                </Center>
              )}

              <ProductList
                isFetching={isFetching}
                fields={fields}
                items={items}
                onMove={move}
                onReorder={handleReorder}
                onRemove={removeProduct}
              />
            </Box>

            <Box flex={1} minW={0}>
              <Text as="b" id="personalization-label">
                Personalisasi
              </Text>
              <PillTabs
                tablist={[{ name: 'Umum' }, { name: 'Spesifik Ship to' }]}
                index={mode === 'shipto' ? 1 : 0}
                onChange={(idx) => {
                  if (!isNewCg3 && idx === 1) {
                    setValue('mode', 'general');
                    return;
                  }
                  setValue('mode', idx === 0 ? 'general' : 'shipto');
                }}
                disabledIndices={!isNewCg3 ? [1] : []}
                aria-labelledby="personalization-label"
              >
                <TabPanels m={2}>
                  <TabPanel>
                    <FiltersGroup />
                  </TabPanel>
                  <TabPanel>
                    <ShipToCSV />
                  </TabPanel>
                </TabPanels>
              </PillTabs>
            </Box>
          </Stack>
        </Stack>

        <Box position="sticky" bottom={0} bg="white">
          <Stack py={2}>
            <Divider borderColor="black" />
          </Stack>
          <Wrap justify="end">
            <WrapItem>
              <HStack>
                <Button
                  size="sm"
                  w={{ base: '28', md: '36' }}
                  isDisabled={isSubmitting}
                  onClick={() => {
                    onClose();
                  }}
                  aria-label="Tutup form highlight"
                >
                  Tutup
                </Button>
                {modalState === 'create' || modalState === 'duplicate' ? (
                  <Button
                    type="submit"
                    variant="solid"
                    size="sm"
                    w={{ base: '28', md: '36' }}
                    isDisabled={isSubmitting || fields.length < 1 || !isValid}
                    isLoading={isSubmitting}
                    aria-label="Tambahkan produk highlight baru"
                  >
                    Tambahkan
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    type="submit"
                    variant="solid"
                    w={{ base: '28', md: '36' }}
                    isDisabled={isSubmitting}
                    isLoading={isSubmitting}
                    aria-label="Simpan perubahan produk highlight"
                  >
                    Simpan
                  </Button>
                )}
              </HStack>
            </WrapItem>
          </Wrap>
        </Box>
      </form>
    </FormProvider>
  );
}
